import { motion } from "framer-motion";
import { useEffect, useRef } from "react";

/**
 * NexiLivingOrb - The signature glowing ring orb
 * Fixed: Touch events on iOS, pointer-events on decorative layers
 */
const NexiLivingOrb = ({ isListening = false, isThinking = false, isError = false, onClick }) => {
  const canvasRef = useRef(null);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const particles = [];
    const particleCount = 30;
    let animationId;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.5 + 0.2
      });
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      const color = isListening ? '16, 185, 129' : isError ? '239, 68, 68' : '6, 182, 212';
      
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;
        
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color}, ${p.opacity})`;
        ctx.fill();
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isListening, isError]);

  // Handle click/tap
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  };

  // Determine ring color
  const getRingColor = () => {
    if (isError) return 'rgba(239, 68, 68, 0.8)';
    if (isListening) return 'rgba(16, 185, 129, 0.8)';
    return 'rgba(6, 182, 212, 0.8)';
  };

  const getGlowColor = () => {
    if (isError) return 'radial-gradient(circle, rgba(239, 68, 68, 0.3) 0%, transparent 70%)';
    if (isListening) return 'radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)';
    return 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)';
  };

  const getShadow = () => {
    if (isError) return '0 0 60px rgba(239, 68, 68, 0.5), 0 0 100px rgba(239, 68, 68, 0.3), inset 0 0 60px rgba(239, 68, 68, 0.2)';
    if (isListening) return '0 0 60px rgba(16, 185, 129, 0.5), 0 0 100px rgba(16, 185, 129, 0.3), inset 0 0 60px rgba(16, 185, 129, 0.2)';
    return '0 0 60px rgba(6, 182, 212, 0.5), 0 0 100px rgba(6, 182, 212, 0.3), inset 0 0 60px rgba(6, 182, 212, 0.2)';
  };

  const getTextGradient = () => {
    if (isError) return 'linear-gradient(135deg, #EF4444 0%, #F97316 50%, #EF4444 100%)';
    if (isListening) return 'linear-gradient(135deg, #10B981 0%, #06B6D4 50%, #10B981 100%)';
    return 'linear-gradient(135deg, #06B6D4 0%, #8B5CF6 50%, #06B6D4 100%)';
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onTouchEnd={handleClick}
      className="relative w-[200px] h-[200px] flex items-center justify-center bg-transparent border-none cursor-pointer p-0 outline-none focus:outline-none"
      style={{ 
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation'
      }}
      data-testid="nexi-living-orb"
      aria-label={isListening ? "Stop listening" : "Tap to speak"}
    >
      {/* Particle canvas - NO POINTER EVENTS */}
      <canvas 
        ref={canvasRef}
        width={280}
        height={280}
        className="absolute inset-0 pointer-events-none"
        style={{ pointerEvents: 'none' }}
        aria-hidden="true"
      />
      
      {/* Outer glow - NO POINTER EVENTS */}
      <motion.div
        className="absolute w-56 h-56 rounded-full pointer-events-none"
        style={{
          background: getGlowColor(),
          pointerEvents: 'none'
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: isListening ? [0.6, 1, 0.6] : isError ? [0.8, 1, 0.8] : [0.5, 0.8, 0.5]
        }}
        transition={{
          duration: isListening || isError ? 1 : 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        aria-hidden="true"
      />
      
      {/* Main ring - clickable area */}
      <motion.div
        className="relative w-44 h-44 rounded-full flex items-center justify-center"
        style={{
          background: 'transparent',
          boxShadow: getShadow(),
          border: `3px solid ${getRingColor()}`
        }}
        animate={isThinking ? {
          rotate: 360
        } : isListening ? {
          scale: [1, 1.05, 1]
        } : {}}
        transition={isThinking ? {
          rotate: { duration: 3, repeat: Infinity, ease: "linear" }
        } : {
          duration: 1,
          repeat: Infinity
        }}
      >
        {/* Inner gradient ring - NO POINTER EVENTS */}
        <div 
          className="absolute inset-2 rounded-full pointer-events-none"
          style={{
            background: isListening
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)'
              : isError
              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            border: `1px solid ${isListening ? 'rgba(16, 185, 129, 0.3)' : isError ? 'rgba(239, 68, 68, 0.3)' : 'rgba(6, 182, 212, 0.3)'}`,
            pointerEvents: 'none'
          }}
          aria-hidden="true"
        />
        
        {/* Center text */}
        <div className="relative z-10 text-center pointer-events-none">
          <motion.span 
            className="text-3xl font-bold tracking-widest"
            style={{
              background: getTextGradient(),
              backgroundSize: '200% 200%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: 'none',
              pointerEvents: 'none'
            }}
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            NEXI
          </motion.span>
        </div>
      </motion.div>
      
      {/* Floating particles - NO POINTER EVENTS */}
      <div className="absolute inset-0 pointer-events-none" style={{ pointerEvents: 'none' }} aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full pointer-events-none"
            style={{
              top: '50%',
              left: '50%',
              backgroundColor: isListening ? '#10B981' : isError ? '#EF4444' : '#06B6D4',
              pointerEvents: 'none'
            }}
            animate={{
              x: [0, Math.cos((i * 60) * Math.PI / 180) * 100],
              y: [0, Math.sin((i * 60) * Math.PI / 180) * 100],
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 0.5,
              ease: "easeOut"
            }}
          />
        ))}
      </div>
    </button>
  );
};

export default NexiLivingOrb;
